import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import { useParams } from "react-router-dom";
import AsyncSelectScroll from "../../../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { usePatientDiagnosesCreate } from "../../../../../../../queries/diagnoses/use-patient-diagnoses-create";
import { usePatientDiagnosesUpdate } from "../../../../../../../queries/diagnoses/use-patient-diagnoses-update";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../../utils/utils";
import {
  DIAGNOSES_INITIAL_VALUE,
  DIAGNOSES_VALIDATION_SCHEMA,
  Diagnoses,
  STATUS_OPTIONS,
  TYPE_OPTIONS,
} from "./diagnosis-constant";

interface UpserDiagnosisDrawerProps {
  disclosureHost: DisclosureHost;
}

const UpsertDiagnosesDrawer = ({
  disclosureHost,
}: UpserDiagnosisDrawerProps) => {
  const { mutateAsync: createDiagnoses } = usePatientDiagnosesCreate();
  const { mutateAsync: updateDiagnoses } = usePatientDiagnosesUpdate();

  const showSnackbar = useSnackbar();
  const { patientId } = useParams();
  const queryClient = useQueryClient();
  const prefillData = disclosureHost?.data;

  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const {
    data: medicalNames,
    isLoading,
    isRefetching,
  } = useGetCataLogCode({
    pagination,
    searchKey: searchKey,
    type: "ICD",
    active: true,
    archive: false,
    enabled: disclosureHost.opened,
  });

  const MEDICAL_CODE_LIST = medicalNames?.data?.content?.map((it) => ({
    uuid: it?.uuid,
    code: `${it?.code} - ${it?.description || ""}`,
  }));

  const formik = useFormik<Diagnoses>({
    initialValues: prefillData || DIAGNOSES_INITIAL_VALUE,
    validationSchema: DIAGNOSES_VALIDATION_SCHEMA,
    onSubmit: handleCreateDiagnoses,
    enableReinitialize: true,
  });

  async function handleCreateDiagnoses(values?: any) {
    const selectedPatient = {
      [patientId as string]: "",
    };

    const medicalCode = {
      ...values?.medicalCode,
      type: "ICD",
    };

    const payload = {
      patientDiagnosis: { ...values, patient: selectedPatient, medicalCode },
      xTENANTID: getXtenantId(),
    };

    if (prefillData) showToastAndResetForm(updateDiagnoses(payload));
    else showToastAndResetForm(createDiagnoses(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-diagnoses-list"],
        });

        closeAndReset();
      },
    });
  }

  function closeAndReset() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle={prefillData ? "Edit Diagnosis" : "Add Diagnosis"}
      dialogAction={
        <StyledButtonV2
          label="Save"
          variant="contained"
          onClick={formik.submitForm}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "700px",
          height: "400px",
        },
      }}
    >
      <Grid container spacing={3} pt={2}>
        <Grid item xs={12}>
          <AsyncSelectScroll
            required
            name="medicalCode"
            handleBlur={formik.handleBlur}
            label="Diagnosis Name"
            placeholder="Select or Search Diagnosis"
            formatKeys={{ val: "uuid", label: "code" }}
            onSearch={(v) => {
              setSearchKey(v);
              setPagination((p) => ({ ...p, page: 0 }));
            }}
            loading={isRefetching || isLoading}
            options={MEDICAL_CODE_LIST}
            pagination={pagination}
            setPagination={setPagination}
            totalElements={medicalNames?.data?.page?.totalElements}
            value={formik.values.medicalCode}
            onChange={(v) => formik.setFieldValue("medicalCode", v)}
            error={formik.touched?.medicalCode && formik.errors.medicalCode}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomSelect
            required
            name="active"
            handleBlur={formik.handleBlur}
            label={"Status"}
            value={formik.values?.active as any}
            options={STATUS_OPTIONS as any}
            onChange={(v) => formik.setFieldValue("active", v)}
            error={formik.touched?.active && formik.errors.active}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomSelect
            label="Type"
            value={formik.values?.type}
            options={TYPE_OPTIONS}
            onChange={(v) => formik.setFieldValue("type", v)}
          />
        </Grid>
        <Grid item xs={12} container>
          <Grid item xs={6}>
            <CustomDatePicker
              maxDate={dayjs(new Date())}
              placeholder="Choose Date"
              value={formik.values.diagnosedDate}
              label="Diagnosed Date"
              onChange={(val) => formik.setFieldValue("diagnosedDate", val)}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Note"
            name="note"
            placeholder="Type here"
            rows={3}
            variant="outlined"
            onBlur={formik.handleBlur}
            value={formik.values?.note}
            onChange={formik.handleChange}
          />
        </Grid>
      </Grid>
    </StyledFormModal>
  );
};

export default UpsertDiagnosesDrawer;
